import React from 'react';
import { Button } from 'antd';
import { Tabs, Switch, Text, Input, Pill } from '@clockout-dev/design-elements';
import SectionCard from '../../common/sectionCard/SectionCard';
import { formatScopeSectionLabel, formatScopeActionLabel } from '../roles.constants';
import styles from '../styles/roles.module.scss';

/**
 * The main panel: every scope in the catalog, grouped into section tabs,
 * with a toggle per scope. `effectiveScopeIds` reflects the unsaved pending
 * edit when there is one, otherwise the role's committed scopes — this is
 * the single source of truth the switches render from.
 *
 * `section`/`action` are purely cosmetic groupings the backend also uses to
 * order the catalog — authorization always checks `key`, never these.
 *
 * `canManage` gates every switch and the Save/Discard actions — someone with
 * only screen:roles:view can browse a role's scopes read-only but never
 * change the assignment (the backend's PUT .../scopes route enforces the
 * same admin:roles:manage requirement independently).
 *
 * Scopes flagged `visible_owner_only` gate capabilities that must never leave
 * the owner role, so they are filtered out of the list entirely unless the
 * owner role is the one selected. Two filters stack here: the catalog endpoint
 * drops them for viewers who aren't owners, and this panel drops them again
 * for every role but the owner's — matching the save endpoint, which refuses
 * to assign one to a non-owner role.
 */
function ScopeAssignmentPanel({
    role,
    catalog,
    effectiveScopeIds,
    search,
    onSearchChange,
    onToggleScope,
    dirty,
    saving,
    onSave,
    onDiscard,
    canManage,
}) {
    // `visible_owner_only` scopes belong to the owner role and nowhere else, so
    // they are only listed while the owner role is the selected one. Viewers
    // who aren't owners never receive them from the catalog endpoint at all.
    const assignableCatalog = role?.is_owner_role
        ? catalog
        : catalog.filter((s) => !s.visible_owner_only);

    // Derived from the catalog itself (already ordered by section, action, key
    // server-side) rather than a hardcoded list, so a brand-new section
    // shipped via migration shows up with no frontend change required. Built
    // from the assignable subset so a section holding only owner-only scopes
    // doesn't render an empty tab on the other roles.
    const sections = [...new Set(assignableCatalog.map((s) => s.section))];
    const [activeSection, setActiveSection] = React.useState(sections[0] || null);

    React.useEffect(() => {
        if ((!activeSection || !sections.includes(activeSection)) && sections.length) {
            setActiveSection(sections[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sections.join(',')]);

    if (!role) {
        return (
            <SectionCard title="Scopes">
                <Text as="p" size="s">Select a role on the left to configure its scopes.</Text>
            </SectionCard>
        );
    }

    const query = (search || '').trim().toLowerCase();
    const scopesInSection = assignableCatalog.filter((s) => s.section === activeSection);
    const filteredScopes = query
        ? scopesInSection.filter((s) => s.key.toLowerCase().includes(query) || (s.description || '').toLowerCase().includes(query))
        : scopesInSection;

    const effectiveSet = new Set(effectiveScopeIds);
    // The owner role can only gain scopes, never lose one it already has —
    // enforced server-side too (see setRoleScopes.js), this just keeps the
    // UI from offering a toggle the save would reject.
    const committedScopeIds = new Set(role.scope_ids || []);
    const lockScope = (scopeId) => role.is_owner_role && committedScopeIds.has(scopeId);

    return (
        <SectionCard
            title={`Scopes for "${role.name}"`}
            action={canManage ? (
                <div className={styles.scopeActions}>
                    {dirty ? (
                        <Button onClick={onDiscard} disabled={saving}>Discard changes</Button>
                    ) : null}
                    <Button type="primary" onClick={onSave} disabled={!dirty || saving} loading={saving}>
                        Save changes
                    </Button>
                </div>
            ) : null}
        >
            {role.description ? (
                <Text as="p" size="s" className={styles.roleDescription}>{role.description}</Text>
            ) : null}

            {effectiveScopeIds.length === 0 ? (
                <div className={styles.emptyWarning}>
                    <Text as="span" size="xs">
                        This role has no scopes yet — anyone holding it will see an empty navigation.
                    </Text>
                </div>
            ) : null}

            {role.is_owner_role ? (
                <div className={styles.ownerNotice}>
                    <Text as="span" size="xs">
                        This is the owner role — scopes it already has can&apos;t be removed, only added to.
                    </Text>
                </div>
            ) : null}

            <Input
                id="scopeSearch"
                size="m"
                placeholder="Search scopes…"
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                className={styles.scopeSearch}
            />

            <Tabs
                items={sections.map((section) => ({ key: section, label: formatScopeSectionLabel(section) }))}
                activeKey={activeSection}
                onChange={(key) => setActiveSection(key)}
            />

            <div className={styles.scopeRows}>
                {filteredScopes.map((scope) => (
                    <div key={scope.id} className={styles.scopeRow}>
                        <div className={styles.scopeRowLabel}>
                            <div className={styles.scopeRowLabelBottom}>
                                <Text as="span" size="s" className={styles.scopeDescription}>{scope.description}</Text>
                                <Pill size="small" text={formatScopeActionLabel(scope.action)} textSize="xs" />
                            </div>
                            <Text as="span" size="xs" className={styles.scopeKey}>{`(${scope.key})`}</Text>
                        </div>
                        <Switch
                            checked={effectiveSet.has(scope.id)}
                            disabled={!canManage || lockScope(scope.id)}
                            onChange={() => onToggleScope(scope.id)}
                        />
                    </div>
                ))}
                {filteredScopes.length === 0 ? (
                    <Text as="p" size="s">No scopes match "{search}".</Text>
                ) : null}
            </div>
        </SectionCard>
    );
}

export default ScopeAssignmentPanel;
